import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Video, PhoneIncoming, PhoneOutgoing, PhoneMissed, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { FullSpinner, Empty } from "@/components/States";
import { toast } from "sonner";

const SCLS = { ringing: "text-amber-700 bg-amber-50", connected: "text-emerald-700 bg-emerald-50", declined: "text-rose-700 bg-rose-50", ended: "text-slate-500 bg-slate-100", missed: "text-rose-700 bg-rose-50" };

export default function Calls() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const load = () => api.get("/calls").then(({ data }) => setData(data));
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []);
  if (!data) return <FullSpinner />;

  const answer = async (cid, action) => {
    try { const { data } = await api.post(`/calls/${cid}/${action}`, {}); if (action === "accept") { toast.success(data.provider_configured ? "Connecting…" : "Secure session created (calling provider not configured yet)."); } load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="calls-page">
      <h1 className="font-serif text-3xl font-bold text-[#58111A] flex items-center gap-2"><Phone className="w-6 h-6 text-[#C59B27]" /> Secure Calls</h1>
      <div className="mj-card p-4 flex items-start gap-2 text-sm text-[#605255]"><ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> ManaJodi calls connect you in-app — your phone number is never shared. {!data.provider_configured && <span className="text-[#B45309]">Live audio/video requires a calling provider to be configured (CALL_PROVIDER_KEY); the request & session flow is ready.</span>}</div>
      {data.calls.length === 0 ? (
        <Empty icon={Phone} title="No calls yet" message="Voice and video calls with your connections will appear here." />
      ) : data.calls.map((c) => {
        const DirIcon = c.status === "missed" ? PhoneMissed : c.direction === "incoming" ? PhoneIncoming : PhoneOutgoing;
        return (
          <div key={c.id} className="mj-card p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 cursor-pointer" onClick={() => nav(`/app/profile/${c.other?.user_id || (c.direction === "incoming" ? c.from_user : c.to_user)}`)}>
              <Avatar photos={c.other?.photos} name={c.other?.full_name} size={9999} className="w-full h-full" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif text-lg font-semibold text-[#261A1C]">{c.other?.full_name}</div>
              <div className="text-xs text-[#605255] flex items-center gap-1.5"><DirIcon className="w-3.5 h-3.5" />{c.kind === "video" ? <Video className="w-3.5 h-3.5" /> : <Phone className="w-3.5 h-3.5" />} {c.direction} {c.kind}</div>
              <span className={`inline-block mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${SCLS[c.status]}`}>{c.status}</span>
            </div>
            {c.status === "ringing" && c.direction === "incoming" && (
              <div className="flex gap-2">
                <button onClick={() => answer(c.id, "accept")} className="btn-primary text-sm py-1.5 px-3">Accept</button>
                <button onClick={() => answer(c.id, "decline")} className="btn-outline text-sm py-1.5 px-3">Decline</button>
              </div>
            )}
            {c.status === "connected" && <button onClick={() => answer(c.id, "end")} className="btn-outline text-sm py-1.5 px-3">End</button>}
          </div>
        );
      })}
    </div>
  );
}
